/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { Locale, LocaleKeys } from '../../../../core/locale';
import { Log } from '../../../../core/logging';
import { ScreenControllerTypes } from '../../../../core/navigation';
import { ScreenTaskControllerTypes } from '../../../../core/navigation/ScreenTaskControllerTypes';
import { CcbSwitch } from '../../../../core/utils/ccb';
import { CcbSwitchName } from '../../../../core/utils/ccb/enums';
import { MetadataType } from '../../../../core/videoplayer/enums';
import { AiringPreviewUtils } from '../../../../core/videoplayer/utils';
import { VideoPlaybackController } from '../../../../core/videoplayer/VideoPlaybackController';
import { setOverlayHighlightVerticalPositionAction } from '../../../overlay/overlayhighlight/overlayhighlight.actions';
import {
    hideOverlayDialog,
    showOverlayDialog,
} from '../../../overlaydialog/overlaydialog.reducers';
import { IOverlayDialog } from '../../../overlaydialog/overlaydialogprops';
import {
    setScreenTaskController,
    showGuideScreen,
    showPreviousScreen,
} from '../../../screens/screens.reducers';

const TAG: string = 'DeleteRecordingOverlay';
const GLOBAL_MOUSE_TRAP: string = 'HideDeleteRecordingOverlay';

export const deleteRecording = () => () => {
    try {
        const playable = VideoPlaybackController.getInstance().getPlayable();
        if (playable) {
            VideoPlaybackController.getInstance().prepareMetadataByType(
                playable,
                MetadataType.DELETE_RECORDING,
            );
        }
    } catch (error) {
        Log.e(TAG, 'stacktrace', error);
    }
};

const onOverlayAutoHide = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;
    if (screenController === ScreenControllerTypes.VIDEO_PLAYER) {
        dispatch(onKeyBackDeleteRecordingOverlay());
    }
};

export const showDeleteRecordingOverlay = () => (dispatch: Function) => {
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.DELETE_RECORDING_OVERLAY_TASKS));
    const { airingOffers, offerPreview } = VideoPlaybackController.getInstance().getMetadata();
    const dataFormatter = new AiringPreviewUtils(airingOffers, offerPreview);
    const subtitle = dataFormatter.getSeasonEpisodeTitle();
    const overlayDialogProps: IOverlayDialog = {
        description: Locale.getString(LocaleKeys.DELETE_RECORDING_CONFIRMATION_MESSAGE),
        hasDimmer: false,
        hasSeperator: true,
        mouseTrapValue: GLOBAL_MOUSE_TRAP,
        options: [
            { text: Locale.getString(LocaleKeys.DELETE_RECORDING_OPTION_DELETE) },
            {
                text: Locale.getString(LocaleKeys.DELETE_RECORDING_OPTION_KEEP_THIS_RECORDING),
            },
        ],
        subtitle,
        title: Locale.getString(LocaleKeys.DELETE_RECORDING_TITLE),
        onAutoHide: () => dispatch(onOverlayAutoHide()),
    };
    dispatch(showOverlayDialog(overlayDialogProps));
    dispatch(setOverlayHighlightVerticalPositionAction(1));
};

/**
 * Below reducers are related to operations on key events.
 */

export const onKeyBackDeleteRecordingOverlay = () => (dispatch: Function) => {
    dispatch(hideOverlayDialog());
    dispatch(showPreviousScreen());
    VideoPlaybackController.getInstance().updateLastViewedPosition(undefined, true);
    VideoPlaybackController.getInstance().switchToLiveTv();
};

export const onKeyOkDeleteRecordingOverlay = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition: overlayHighlightVerticalPosition } = getState().overlayHighlight;
    if (overlayHighlightVerticalPosition === 0) {
        dispatch(deleteRecording());
    } else {
        dispatch(onKeyBackDeleteRecordingOverlay());
    }
};
